import { BehaviorSubject, Observable, Subject } from "rxjs";
import { map, tap } from "rxjs/operators";

import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";

import { environment } from "../../../../environments/environment";
import { Task } from "../domains/task";

export interface ObjectTasksSummary {
  active: number;
  overdue: number;
  next_title: string | null;
  next_due_date: string | null;
}

@Injectable({
  providedIn: "root",
})
export class TaskService {
  url = environment.apiUrl + "/tasks";

  private tasksSubject = new BehaviorSubject<Task[]>([]);
  public tasks$ = this.tasksSubject.asObservable();

  /** Émis après chaque création, modification ou suppression : les compteurs des fiches se rafraîchissent */
  readonly tasksChanged$ = new Subject<void>();

  constructor(private http: HttpClient) {}

  createTask(task: any): Observable<Task> {
    return this.http.post<any>(this.url, task).pipe(
      map(Task.createInstance),
      tap(() => this.tasksChanged$.next()),
    );
  }

  getTask(taskId: string): Observable<Task> {
    return this.http.get<any>(`${this.url}/${taskId}`).pipe(map(Task.createInstance));
  }

  updateTask(taskId: string, task: any): Observable<Task> {
    return this.http.put<any>(`${this.url}/${taskId}`, task).pipe(
      map(Task.createInstance),
      tap(() => this.tasksChanged$.next()),
    );
  }

  deleteTask(taskId: string): Observable<any> {
    return this.http.delete<any>(`${this.url}/${taskId}`).pipe(tap(() => this.tasksChanged$.next()));
  }

  filterTasks(filters: any): Observable<any> {
    return this.http.post<any>(`${this.url}/filter`, filters).pipe(
      map((res) => {
        if (res.tasks) {
          res.tasks = Task.createInstances(res.tasks);
        }
        if (res.groups) {
          res.groups = res.groups.map((g: any) => ({
            ...g,
            tasks: Task.createInstances(g.tasks),
          }));
        }
        return res;
      }),
      tap((res) => {
        if (res.tasks) {
          this.tasksSubject.next(res.tasks);
        }
      }),
    );
  }

  getMyTasks(): Observable<Task[]> {
    return this.http.get<any[]>(`${this.url}/my`).pipe(map(Task.createInstances));
  }

  /** Résumé par objet pour les colonnes « Tâches » des listes : un seul appel pour toute la page */
  getTaskCountsByObjects(objectType: string, objectIds: string[]): Observable<Record<string, ObjectTasksSummary>> {
    return this.http.post<Record<string, ObjectTasksSummary>>(`${this.url}/counts-by-objects`, {
      object_type: objectType,
      object_ids: objectIds,
    });
  }

  getTasksByObject(objectType: string, objectId: string): Observable<Task[]> {
    return this.http.get<any[]>(`${this.url}/by-object/${objectType}/${objectId}`).pipe(map(Task.createInstances));
  }

  updateTaskStatus(taskId: string, status: string): Observable<Task> {
    return this.http.put<any>(`${this.url}/${taskId}/status`, { status }).pipe(
      map(Task.createInstance),
      tap(() => this.tasksChanged$.next()),
    );
  }

  updateTaskAssignment(taskId: string, assignedTo: any[]): Observable<Task> {
    return this.http
      .put<any>(`${this.url}/${taskId}/assign`, { assigned_to: assignedTo })
      .pipe(map(Task.createInstance));
  }

  /**
   * Recupere la liste des objets lies ayant des taches actives.
   * Endpoint : GET /tasks/linked-objects
   * Retourne : [{object_id, object_type, object_name, task_count}]
   */
  getLinkedObjects(viewMode: "all" | "mine" = "mine"): Observable<any[]> {
    return this.http.get<any[]>(`${this.url}/linked-objects`, {
      params: { view_mode: viewMode },
    });
  }
}
